import { ElementProps } from '@/components/sections/builder/types';
import { WrapperChild } from '@/components/sections/builder/wrapper';
import { ElementDataType } from '@/components/sections/builder/elements/body/multicolumn/type';
import { cn } from '@/lib/utils';
import Link from 'next/link';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel';
import { useSearchParams } from 'next/navigation';

export default function ElementContent({ element, preview }: ElementProps) {
  const query = useSearchParams();
  const themeId = query.get('theme_id') || '';

  const content = element.data as ElementDataType;

  const childs = element.childs;

  const isExternalLink = content.buttonLink?.includes('http');

  return (
    <>
      <section className={cn('multicolumn_wrapper')}>
        <div className='mx-auto duration-100 theme_layout_page_width'>
          <div className='relative multicolumn-padding'>
            <div className='relative px-6 py-6 overflow-hidden lg:px-6 lg:py-8 '>
              <div
                className={cn(
                  content.contentAlign === 'left' && '[&>*]:text-left',
                  content.contentAlign === 'center' && '[&>*]:text-center',
                  content.contentAlign === 'right' && '[&>*]:text-right'
                )}
              >
                <h2
                  className={cn(
                    'multicolumn_heading theme_font_heading mb-6  break-all font-extrabold tracking-tight text-gray-900 lg:text-4xl'
                  )}
                  dangerouslySetInnerHTML={{ __html: content.heading }}
                ></h2>
              </div>

              {(!content.enableSwipeMobile || content.enableSwipeMobile) && (
                <div
                  className={cn(
                    'theme_layout_space_grid_horizontal theme_layout_space_grid_vertical gap-y-2',
                    content.enableSwipeMobile ? 'hidden md:grid' : 'grid',
                    `sm:grid-cols-${content.numberOfColumns - 1}`,
                    `md:grid-cols-${content.numberOfColumns - 1}`,
                    `lg:grid-cols-${content.numberOfColumns}`,
                    content.totalColMobile === 'two_columns' ? 'grid-cols-2' : 'grid-cols-1'
                  )}
                >
                  {childs?.map((child) => (
                    <WrapperChild
                      key={child.id}
                      child={{
                        ...child,
                        parentData: content,
                        themeId,
                      }}
                      parentType={element.type}
                      preview={preview}
                    />
                  ))}
                </div>
              )}

              {content.enableSwipeMobile && (
                <div className='block theme_layout_space_grid_horizontal theme_layout_space_grid_vertical place-content-center md:hidden'>
                  <Carousel className='max-w-full' opts={{ loop: true }}>
                    <CarouselContent>
                      {childs?.map((child) => (
                        <CarouselItem
                          key={child.id}
                          className={cn(
                            content.totalColMobile === 'two_columns' ? 'basis-1/2' : 'basis-full'
                          )}
                        >
                          <WrapperChild
                            child={{
                              ...child,
                              parentData: content,
                              themeId,
                            }}
                            parentType={element.type}
                            preview={preview}
                          />
                        </CarouselItem>
                      ))}
                    </CarouselContent>
                    <CarouselPrevious className='left-4 h-12 w-12 [&>svg]:h-6 [&>svg]:w-6' />
                    <CarouselNext className='right-4 h-12 w-12 [&>svg]:h-6 [&>svg]:w-6' />
                  </Carousel>
                </div>
              )}

              <div className='flex justify-center mt-3'>
                {content.buttonLabel && (
                  <Link
                    href={
                      isExternalLink
                        ? content.buttonLink || '#'
                        : `/${(content.buttonLink || '').replace(/^\//, '')}${
                            preview ? `?theme_id=${themeId}&isPreview=true} ` : ''
                          }`
                    }
                  >
                    <button
                      className='flex justify-center py-3 text-base rounded-sm button_color_scheme_multi items- w-52'
                      type='button'
                    >
                      {content.buttonLabel}
                    </button>
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
      <style jsx>
        {`
          .multicolumn_wrapper {
            background-color: ${content.colorScheme?.background || 'white'};
            color: ${content.colorScheme?.text || '#52535B'};
          }
          .button_color_scheme_multi {
            background-color: ${content.colorScheme?.btnSolidBackgroud} !important;
            color: ${content.colorScheme?.btnSolidlabel};
            box-shadow: 0.5px 0.5px 2px ${content.colorScheme?.shadow};
          }
          @media (min-width: 768px) {
            .multicolumn-padding {
              padding-top: ${content.topPadding || 0}px;
              padding-bottom: ${content.bottomPadding || 0}px;
            }
          }
          .multicolumn_heading {
            font-size: ${content.headingSize}px;
            line-height: ${content.headingSize}px;
            color: ${content.colorScheme?.text || '#52535B'};
          }
        `}
      </style>
    </>
  );
}
